/**
 * Avatar dropdown 账户状态卡数据模型。纯函数 — 无 DOM — 便于独立单测，
 * avatarView 只是薄适配层。5 态（Guest/Free/Lite/Pro/Max）各回答三个问题：
 * 我是谁（身份徽章，见 planBadge.ts）/ 我能用多少（右侧信息位 + 进度条）/
 * 我下一步该做什么（底部钩子）。
 *
 * 右侧信息位（用量读数）纪律（2026-08-19 round-3 修订）：
 * - Guest/Free/Lite **不渲染右上角信息位** —— 底部钩子已承载登录/升级
 *   动作。Guest 钩子也改为 Free 同款 accent 链接样式（用户反馈）。pill
 *   字段仅 Pro/Max 有值。
 * - Pro/Max 右上角是**用量读数（非胶囊）**："0 / 150 used"，前面带引导图标
 *   （round-4 定稿：**Pro 恒 gauge 仪表盘 / Max 恒 zap 闪电**，闪电悬停提示
 *   "Priority generation"）；进度条填充 = used/limit，从 0 向上增。同步兜底
 *   used=0（"0 / 150 used"，契合"从 0 计数"）；异步配额返回后换成真实用量。
 *   旧实现 "150 / 150 left" 让新用户误以为额度已用光。
 * - Pro/Max 的 detail 行右侧携带重置日（"Resets monthly"，异步换成真实
 *   日期，同行动一个居左一个居右）；底部行默认空 — Max 的 "Priority
 *   generation" 已上移为读数前的闪电图标（round-4），Pro 底部仅剩余 ≤20%
 *   时异步填 Max upsell。
 *
 * 文案纪律（只描述"该档确定能做什么"）：
 * - Lite 权益描述 = "Every public topic · read · listen · watch"。解释口径：
 *   GitHub Trending 不属于 public topics（独立分类/导航）。GitHub Deep Dives
 *   基线是 Pro 专属（plan_entitlements.github_deep_dive: lite=0, pro/max=1），
 *   但限时 bonus 期间 Lite 也置 1（2026-09-20 起 test/prod 均已开启）——
 *   paintRow 据此切换底部钩子文案：Lite 已含 GitHub 时不再把它列为升级理由。
 * - 禁用 "full" / "full library" 这类全量承诺措辞（用户明确指出的歧义点）——
 *   广度统一用 "Every public topic"，绝不用 full。
 * - 钩子只用增益框架（下一档多给什么），不描述当前档缺什么。
 * - Pro 的 Max 推销仅当剩余额度 ≤20% 时出现（shouldNudgeMax），常态只显示
 *   重置日；Max 零推销，纯地位强化。
 */

export type Plan = 'free' | 'lite' | 'pro' | 'max';

/** 状态卡底部钩子行的语气：accent=蓝色 CTA / teal=青色 CTA / gold=金色 upsell /
 *  neutral=中性信息（无箭头）。 */
export type FooterTone = 'accent' | 'teal' | 'gold' | 'neutral';

export interface AvatarStatusCard {
  plan: Plan;
  /** 第二行：当前身份的一句话状态。 */
  detail: string;
  /** Pro/Max：detail 行右侧元信息（重置日；同步 "Resets monthly"，异步补丁换成真实日期）。 */
  detailMeta: string;
  /** 右上角用量读数：仅 Pro/Max 有值（"0 / 150 used" 从 0 计数，异步换成真实
   *  用量）；Guest/Free/Lite 为空串（右上角 CTA 已删除，动作由底部钩子承载）。 */
  pill: string;
  /** 读数的引导图标（round-4 定稿）：Pro 恒 gauge（仪表盘=配额），Max 恒 zap
   *  （闪电=priority generation，悬停提示见 priorityLabel）。图标只出现在
   *  hasQuota 卡上，且跨异步 patch 保持（patch 只换尾部文本，不重写图标）。 */
  readoutIcon: 'gauge' | 'zap';
  /** readoutIcon === 'zap' 时的悬停 tooltip 文案（"Priority generation"）。 */
  priorityLabel: string;
  /** 底部钩子/信息行文案（不含箭头 — 箭头由视图层补）。Pro/Max 默认空串
   *  （重置已上移 detail 行；仅 Pro 低额度时异步填 Max upsell）。 */
  footer: string;
  footerTone: FooterTone;
  /** Pro/Max 才显示进度条。 */
  hasQuota: boolean;
  /** 同步兜底填充比例 0..1（配额 API 未返回前按 0 用量显示，契合"从 0 计数"）。 */
  progress: number;
  /** true = Pro 剩余 ≤20%，底部钩子换成 Max upsell。 */
  maxNudge: boolean;
}

/** Max 剩余 ≤20% 是 Max upsell 的唯一触发条件。 */
export function shouldNudgeMax(remaining: number, total: number): boolean {
  return total > 0 && remaining / total <= 0.2;
}

/** 实时额度 pill 文案（用量计数，从 0 向上增）："3 / 150 used"。 */
export function formatQuotaPill(used: number, total: number): string {
  return Math.max(0, used) + ' / ' + total + ' used';
}

/** cycleEnd ISO → "Sep 1"（UTC，避免午夜 UTC 授权在美西显示成前一天）。 */
export function formatResetDate(cycleEnd: string | null): string {
  if (!cycleEnd) return '';
  const date = new Date(cycleEnd);
  if (!Number.isFinite(date.getTime())) return '';
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
}

/** Guest 卡（登录引导，不卖套餐）。右上角无信息位 — 登录动作由底部钩子承载，
 *  钩子用 Free 同款 accent 链接样式（round-3 用户反馈：做成超链样式）。 */
export function paintGuestRow(): AvatarStatusCard {
  return {
    plan: 'free',
    detail: 'Explore public content',
    detailMeta: '',
    pill: '',
    readoutIcon: 'gauge',
    priorityLabel: '',
    footer: 'Sign in to save your progress',
    footerTone: 'accent',
    hasQuota: false,
    progress: 0,
    maxNudge: false,
  };
}

export function paintRow(plan: Plan, privateChaptersPerMonth = 0, githubDeepDive = false): AvatarStatusCard {
  const quota = Number.isSafeInteger(privateChaptersPerMonth) && privateChaptersPerMonth >= 0 ? privateChaptersPerMonth : 0;
  if (plan === 'max') {
    return {
      plan,
      detail: 'AI private chapters',
      detailMeta: 'Resets monthly',
      pill: formatQuotaPill(0, quota),
      // round-4: Max 的「Priority generation」从底部文案改为读数前的闪电图标，
      // 悬停 tooltip 提示（priorityLabel）。底部行不再有 Max 专属文案。
      readoutIcon: 'zap',
      priorityLabel: 'Priority generation',
      footer: '',
      footerTone: 'neutral',
      hasQuota: true,
      progress: 0,
      maxNudge: false,
    };
  }
  if (plan === 'pro') {
    return {
      plan,
      detail: 'AI private chapters',
      detailMeta: 'Resets monthly',
      pill: formatQuotaPill(0, quota),
      readoutIcon: 'gauge',
      priorityLabel: '',
      footer: '',
      footerTone: 'neutral',
      hasQuota: true,
      progress: 0,
      maxNudge: false,
    };
  }
  if (plan === 'lite') {
    return {
      plan,
      detail: 'Every public topic · read · listen · watch',
      detailMeta: '',
      pill: '',
      readoutIcon: 'gauge',
      priorityLabel: '',
		footer: githubDeepDive ? 'Create private AI chapters with Pro' : 'Create private AI chapters & GitHub Deep Dives with Pro',
      footerTone: 'teal',
      hasQuota: false,
      progress: 0,
      maxNudge: false,
    };
  }
  return {
    plan,
    detail: 'Explore public content',
    detailMeta: '',
    pill: '',
    readoutIcon: 'gauge',
    priorityLabel: '',
    footer: 'Unlock every public topic with Lite',
    footerTone: 'accent',
    hasQuota: false,
    progress: 0,
    maxNudge: false,
  };
}
